import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { posts } from '@/constants/content'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { RevealImage } from '@/components/motion/reveal-image'

const featured = posts.slice(0, 2)

export const BlogPreview: FC = (): ReactNode => {
    return (
        <section
            id='journal'
            className='edge bg-background py-20 md:py-28'
            aria-labelledby='journal-heading'
        >
            <div className='mx-auto max-w-[110rem]'>
                <p className='label mb-10 text-muted-foreground'>Photographer’s diary</p>

                <div className='grid gap-10 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-16'>
                    <div className='lg:sticky lg:top-28 lg:self-start'>
                        <SplitText as='h2' lines={['My blog']} className='display-xl' />

                        <span id='journal-heading' className='sr-only'>
                            Latest writing
                        </span>

                        <Reveal delay={0.1}>
                            <p className='mt-6 max-w-xs text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                Curious about what actually goes into a good photoshoot? Notes,
                                practical advice and the occasional honest post-mortem.
                            </p>

                            <Link
                                href='/blog'
                                className='pill mt-8 border border-border hover:bg-foreground hover:text-background'
                            >
                                View all
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                        </Reveal>
                    </div>

                    <ul className='grid gap-4 sm:grid-cols-2'>
                        {featured.map((post, index) => (
                            <li key={post.slug}>
                                <Link
                                    href={`/blog/${post.slug}`}
                                    className='group relative block overflow-hidden rounded-[var(--radius-lg)]'
                                >
                                    <RevealImage
                                        src={post.image}
                                        alt={post.title}
                                        className='aspect-[3/4]'
                                        imageClassName='transition-transform duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]'
                                        sizes='(max-width: 640px) 100vw, 40vw'
                                        delay={index * 0.1}
                                    />

                                    <span
                                        className='pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent'
                                        aria-hidden='true'
                                    />

                                    <span className='pointer-events-none absolute inset-x-0 bottom-0 p-6 text-white'>
                                        <span className='label block text-white/70'>{post.date}</span>

                                        <span className='mt-2 block text-[1.0625rem] font-medium leading-snug'>
                                            {post.title}
                                        </span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}
